import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, HelpCircle, Phone, ArrowRight, BookOpen, Smile, AlertCircle } from 'lucide-react';
import { TrustBadge } from '../components/TrustBadge';
import { StepGuide } from '../components/StepGuide';
import { FAQAccordion } from '../components/FAQAccordion';
import { SocialProofPopup } from '../components/SocialProofPopup';
export function SalesPage() {
  useEffect(() => {
    document.title = 'Aprendé a usar tu celular sin miedo y sin pedir ayuda';
  }, []);
  const scrollToPurchase = () => {
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-800 pb-20" data-id="element-83">
      {/* NAV - Simple & Trustworthy */}
      <nav className="bg-white py-4 px-4 border-b border-slate-100 sticky top-0 z-50" data-id="element-84">
        <div className="max-w-5xl mx-auto flex justify-between items-center" data-id="element-85">
          <div className="flex items-center gap-2" data-id="element-86">
            <BookOpen className="w-6 h-6 text-blue-600" data-id="element-87" />
            <span className="font-bold text-slate-900 text-lg" data-id="element-88">
              Celular Fácil
            </span>
          </div>
          <button onClick={scrollToPurchase} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors" data-id="element-89">
            Quiero el manual
          </button>
        </div>
      </nav>

      {/* A) HERO SECTION - Calm, Direct, Reassuring */}
      <header className="pt-12 pb-16 px-4 bg-white" data-id="element-90">
        <div className="max-w-4xl mx-auto text-center" data-id="element-91">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-92">
            <span className="inline-block bg-blue-50 text-blue-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-93">
              Especial para mayores de 50 años
            </span>

            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 leading-tight mb-6" data-id="element-94">
              Aprendé a usar tu celular <br className="hidden md:block" data-id="element-95" />
              <span className="text-blue-600" data-id="element-96">sin miedo y sin pedir ayuda</span>
            </h1>

            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-97">
              Un manual paso a paso, escrito en lenguaje simple. No necesitás
              saber nada de tecnología para empezar.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12" data-id="element-98">
              <button onClick={scrollToPurchase} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-lg font-bold py-4 px-8 rounded-xl shadow-lg shadow-blue-100 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-99">
                Ver el manual ahora <ArrowRight className="w-5 h-5" data-id="element-100" />
              </button>
              <p className="text-sm text-slate-500 font-medium" data-id="element-101">
                Lectura digital inmediata
              </p>
            </div>

            <div className="max-w-3xl mx-auto" data-id="element-102">
              <img src="https://cdn.magicpatterns.com/uploads/qFYHVew2Fp41i7vsjHK3Do/ChatGPT_Image_11_ene_2026,_16_33_43.png" alt="Manual de uso del celular para adultos mayores" className="rounded-2xl shadow-2xl border-4 border-white mx-auto" data-id="element-103" />
            </div>
          </motion.div>
        </div>
      </header>

      {/* B) IDENTIFICACIÓN EMOCIONAL - Validating frustrations */}
      <section className="py-16 px-4 bg-[#F0F4F8]" data-id="element-104">
        <div className="max-w-3xl mx-auto" data-id="element-105">
          <div className="text-center mb-12" data-id="element-106">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" data-id="element-107">
              ¿Te sentís identificado con esto?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-108">
              Sabemos que la tecnología puede ser frustrante. No es tu culpa.
            </p>
          </div>

          <div className="space-y-6" data-id="element-109">
            {[{
            text: 'Me da miedo tocar algo y romper el celular o borrar cosas importantes.',
            icon: AlertCircle,
            color: 'text-orange-500',
            bg: 'bg-orange-50'
          }, {
            text: 'No entiendo los botones ni qué significan esos íconos raros.',
            icon: HelpCircle,
            color: 'text-blue-500',
            bg: 'bg-blue-50'
          }, {
            text: 'Siempre tengo que esperar a que mis hijos o nietos tengan tiempo de ayudarme.',
            icon: Phone,
            color: 'text-purple-500',
            bg: 'bg-purple-50'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-sm border border-slate-100" data-id="element-110">
                <div className={`${item.bg} p-3 rounded-full flex-shrink-0`} data-id="element-111">
                  <item.icon className={`w-6 h-6 ${item.color}`} data-id="element-112" />
                </div>
                <p className="text-lg text-slate-700 font-medium pt-1" data-id="element-113">
                  "{item.text}"
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* C) SOLUCIÓN CLARA - The Bridge */}
      <section className="py-16 px-4 bg-white" data-id="element-114">
        <div className="max-w-4xl mx-auto" data-id="element-115">
          <div className="bg-blue-50 rounded-3xl p-8 md:p-12 border border-blue-100" data-id="element-116">
            <div className="text-center mb-10" data-id="element-117">
              <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-118">
                La solución pensada para vos
              </h2>
              <p className="text-xl text-slate-700" data-id="element-119">
                Creamos el manual{' '}
                <span className="font-bold text-blue-700" data-id="element-120">
                  "Celular Sin Ayuda"
                </span>
                . No es un curso técnico, es una guía amigable.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-121">
              <div className="space-y-4" data-id="element-122">
                <div className="flex items-start gap-3" data-id="element-123">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-124" />
                  <div data-id="element-125">
                    <h3 className="font-bold text-slate-900" data-id="element-126">
                      Paso a paso con imágenes
                    </h3>
                    <p className="text-slate-600" data-id="element-127">
                      Cada instrucción tiene una foto que te muestra exactamente
                      dónde tocar.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-128">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-129" />
                  <div data-id="element-130">
                    <h3 className="font-bold text-slate-900" data-id="element-131">
                      Lenguaje 100% claro
                    </h3>
                    <p className="text-slate-600" data-id="element-132">
                      Sin palabras en inglés ni términos complicados. Hablamos
                      tu idioma.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-133">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-134" />
                  <div data-id="element-135">
                    <h3 className="font-bold text-slate-900" data-id="element-136">
                      Digital y fácil de leer
                    </h3>
                    <p className="text-slate-600" data-id="element-137">
                      Lo recibís en tu email y lo podés leer en la computadora,
                      tablet o el mismo celular.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col justify-center items-center text-center" data-id="element-138">
                <Smile className="w-16 h-16 text-yellow-400 mb-4" data-id="element-139" />
                <p className="text-lg font-medium text-slate-800" data-id="element-140">
                  "Por fin alguien me explica con paciencia y claridad. Ya no
                  tengo miedo de usar mi teléfono."
                </p>
                <p className="text-slate-500 mt-4 text-sm" data-id="element-141">- Marta, 64 años</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* D) QUÉ INCLUYE - Ultra Simple */}
      <section className="py-16 px-4 bg-[#F8FAFC]" data-id="element-142">
        <div className="max-w-4xl mx-auto" data-id="element-143">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-900 mb-10" data-id="element-144">
            Todo lo que vas a aprender
          </h2>

          <div className="grid sm:grid-cols-2 gap-4" data-id="element-145">
            {['Cómo mandar audios y fotos por WhatsApp', 'Cómo hacer videollamadas con tu familia', 'Cómo sacar fotos lindas y borrarlas si salen mal', 'Cómo buscar recetas, noticias o remedios en Google', 'Cómo agrandar la letra para leer mejor', 'Cómo evitar estafas y usar el celular seguro'].map((item, i) => <div key={i} className="bg-white p-4 rounded-lg border border-slate-200 flex items-center gap-3" data-id="element-146">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-147"></div>
                <p className="text-slate-700 font-medium" data-id="element-148">{item}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* E) CÓMO SE USA - Crucial for closing */}
      <section className="py-16 px-4 bg-white" data-id="element-149">
        <div className="max-w-3xl mx-auto" data-id="element-150">
          <div className="text-center mb-12" data-id="element-151">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" data-id="element-152">
              ¿Cómo obtengo el manual?
            </h2>
            <p className="text-lg text-slate-600" data-id="element-153">
              Es un proceso muy sencillo y seguro. Te guiamos paso a paso:
            </p>
          </div>

          <StepGuide data-id="element-154" />
        </div>
      </section>

      {/* SUPPORT SECTION - NEW */}
      <section className="py-16 px-4 bg-[#F0F4F8]" data-id="element-155">
        <div className="max-w-3xl mx-auto" data-id="element-156">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-blue-100 shadow-sm" data-id="element-157">
            <div className="text-center mb-8" data-id="element-158">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4" data-id="element-159">
                <HelpCircle className="w-8 h-8 text-blue-600" data-id="element-160" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3" data-id="element-161">
                Soporte técnico durante 30 días
              </h2>
              <p className="text-lg text-slate-600" data-id="element-162">
                No estás solo. Te acompañamos en tu aprendizaje.
              </p>
            </div>

            <div className="space-y-6" data-id="element-163">
              <div className="flex items-start gap-4" data-id="element-164">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center" data-id="element-165">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-166" />
                </div>
                <div data-id="element-167">
                  <h3 className="font-bold text-slate-900 mb-1" data-id="element-168">
                    ¿Te quedó alguna duda del manual?
                  </h3>
                  <p className="text-slate-600" data-id="element-169">
                    Escribinos por email y te explicamos de nuevo, con paciencia
                    y claridad.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-170">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center" data-id="element-171">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-172" />
                </div>
                <div data-id="element-173">
                  <h3 className="font-bold text-slate-900 mb-1" data-id="element-174">
                    ¿No podés hacer algo en tu celular?
                  </h3>
                  <p className="text-slate-600" data-id="element-175">
                    Contanos qué te pasa y te guiamos paso a paso para
                    resolverlo.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-176">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center" data-id="element-177">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-178" />
                </div>
                <div data-id="element-179">
                  <h3 className="font-bold text-slate-900 mb-1" data-id="element-180">
                    Respuestas en lenguaje simple
                  </h3>
                  <p className="text-slate-600" data-id="element-181">
                    Te respondemos en 24-48 horas, siempre con palabras claras y
                    sin apuro.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-182">
              <p className="text-center text-slate-700 font-medium" data-id="element-183">
                📧 Tenés 30 días completos de soporte incluido con tu compra
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-184">
        <div className="max-w-4xl mx-auto" data-id="element-185">
          <div className="text-center mb-12" data-id="element-186">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-187">
              Además, recibís estos regalos
            </h2>
            <p className="text-xl text-slate-600" data-id="element-188">
              Guías adicionales incluidas con tu compra
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-189">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/1NM2LF7S3LVZBWx5cXQyr6/ChatGPT_Image_11_ene_2026,_17_36_21.png',
            title: 'IA Fácil en tu Celular',
            desc: 'Aprendé a usar ChatGPT para que te ayude a escribir mensajes, buscar recetas o resumir textos.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/2ucM1GD4B79DLRsVEXL2vS/ChatGPT_Image_11_ene_2026,_17_37_01.png',
            title: 'Mercado Pago Experto',
            desc: 'La guía definitiva para comprar, pagar servicios, transferir dinero y cobrar sin miedos.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/dcS9kBUAbjmdLzArkwN6bz/ChatGPT_Image_11_ene_2026,_17_38_26.png',
            title: 'Guía Anti-Estafas',
            desc: 'Protegé tu dinero. Aprendé a detectar mensajes falsos y llamadas sospechosas al instante.'
          }].map((bonus, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-slate-50 rounded-2xl p-6 border border-slate-200" data-id="element-190">
                <div className="mb-4 overflow-hidden rounded-xl" data-id="element-191">
                  <img src={bonus.img} alt={bonus.title} className="w-full h-auto" data-id="element-192" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-193">
                  {bonus.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-194">
                  {bonus.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* F) CONFIANZA Y SEGURIDAD + PRICING */}
      <section id="compra" className="py-16 px-4 bg-slate-900 text-white" data-id="element-195">
        <div className="max-w-6xl mx-auto" data-id="element-196">
          <div className="text-center mb-8" data-id="element-197">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-198">
              Elegí la opción ideal para vos
            </h2>
            <p className="text-slate-300 text-lg mb-6" data-id="element-199">
              Empezá hoy mismo a usar tu celular con confianza.
            </p>

            {/* Secure Payment Badge - Above */}
            <div className="flex flex-col items-center gap-2 mb-8" data-id="element-200">
              <p className="text-slate-300 text-sm font-medium" data-id="element-201">
                Pago 100% seguro procesado por:
              </p>
              <img src="https://cdn.magicpatterns.com/uploads/avCwRnrSTuiqXUwcSckbkD/ML.jpg" alt="Mercado Libre - Pago Seguro" className="h-10 w-auto" data-id="element-202" />
            </div>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start" data-id="element-203">
            {/* COMBO 1 */}
            <div className="bg-white rounded-2xl overflow-hidden text-slate-900 flex flex-col h-full shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-204">
              <div className="p-6 flex-1 flex flex-col" data-id="element-205">
                <div className="mb-6" data-id="element-206">
                  <img src="https://cdn.magicpatterns.com/uploads/wDrDrrNBBcyaCgRFoJttih/ChatGPT_Image_21_ene_2026,_16_37_54.png" alt="Combo Esencial" className="w-full h-48 object-contain mb-4" data-id="element-207" />
                  <div className="inline-block bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-208">
                    COMBO 1
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-209">
                    Aprender a usar el celular sin ayuda
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-210">
                    Ideal para aprender a usar el celular desde cero, con
                    explicaciones claras y sin depender de otros.
                  </p>
                </div>

                <div className="mb-6" data-id="element-211">
                  <div className="flex items-baseline gap-1" data-id="element-212">
                    <span className="text-4xl font-bold text-slate-900" data-id="element-213">
                      $19.990
                    </span>
                    <span className="text-slate-500 text-sm" data-id="element-214">ARS</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-1" data-id="element-215">
                  {['Manual principal: Aprender a usar el celular sin ayuda', 'Guía: Cómo usar ChatGPT de forma fácil', 'Guía: Mercado Libre y Mercado Pago simple', 'Guía anti estafas digitales', 'Soporte técnico por email durante 30 días'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-216">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-217" />
                      <span className="text-slate-700 text-sm" data-id="element-218">{feature}</span>
                    </li>)}
                </ul>

                <a href="https://checkout3.rosetadigital.com/cart/45857309196473:1" className="block w-full bg-green-600 hover:bg-green-700 text-white text-center font-bold py-3 rounded-xl transition-colors" data-id="element-219">
                  Elegir este combo
                </a>
              </div>
            </div>

            {/* COMBO 2 - RECOMENDADO */}
            <div className="relative bg-white rounded-2xl overflow-hidden text-slate-900 flex flex-col h-full shadow-2xl transform md:-translate-y-4 border-2 border-blue-400 z-10" data-id="element-220">
              <div className="absolute top-0 right-0 bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-bl-xl z-20" data-id="element-221">
                RECOMENDADO
              </div>
              <div className="p-6 flex-1 flex flex-col" data-id="element-222">
                <div className="mb-6" data-id="element-223">
                  <img src="https://cdn.magicpatterns.com/uploads/2XoyMVqvnxs3SKoyQzz7Fr/ChatGPT_Image_21_ene_2026,_17_00_15.png" alt="Combo Problemas Comunes" className="w-full h-48 object-contain mb-4" data-id="element-224" />
                  <div className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-225">
                    COMBO 2
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-226">
                    Combo 1 + Errores comunes del celular y cómo solucionarlos
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-227">
                    Ideal si querés aprender qué hacer cuando algo no funciona,
                    entender los errores más comunes y usar el celular con más
                    seguridad.
                  </p>
                </div>

                <div className="mb-6" data-id="element-228">
                  <div className="flex items-baseline gap-1" data-id="element-229">
                    <span className="text-4xl font-bold text-blue-600" data-id="element-230">
                      $29.990
                    </span>
                    <span className="text-slate-500 text-sm" data-id="element-231">ARS</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-1" data-id="element-232">
                  {['Todo lo incluido en el Combo 1', 'Manual extra: Errores comunes del celular y cómo solucionarlos sin miedo', 'Soporte técnico por email durante 30 días'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-233">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-234" />
                      <span className="text-slate-700 text-sm font-medium" data-id="element-235">
                        {feature}
                      </span>
                    </li>)}
                </ul>

                <a href="https://checkout3.rosetadigital.com/cart/45927673790649:1" className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center font-bold py-4 rounded-xl shadow-lg transition-colors" data-id="element-236">
                  Elegir este combo
                </a>
              </div>
            </div>

            {/* COMBO 3 */}
            <div className="bg-white rounded-2xl overflow-hidden text-slate-900 flex flex-col h-full shadow-lg hover:shadow-xl transition-shadow duration-300" data-id="element-237">
              <div className="p-6 flex-1 flex flex-col" data-id="element-238">
                <div className="mb-6" data-id="element-239">
                  <img src="https://cdn.magicpatterns.com/uploads/7KePXEa3qYS1vCcUHfMchs/ChatGPT_Image_21_ene_2026,_18_03_35.png" alt="Combo Redes Sociales" className="w-full h-48 object-contain mb-4" data-id="element-240" />
                  <div className="inline-block bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-241">
                    COMBO 3
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-242">
                    Combo 1 y 2 + Usar Facebook e Instagram sin confundirte
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-243">
                    Ideal si además querés usar Facebook e Instagram con
                    tranquilidad, sin miedo a tocar algo mal o exponerte de más.
                  </p>
                </div>

                <div className="mb-6" data-id="element-244">
                  <div className="flex items-baseline gap-1" data-id="element-245">
                    <span className="text-4xl font-bold text-slate-900" data-id="element-246">
                      $39.990
                    </span>
                    <span className="text-slate-500 text-sm" data-id="element-247">ARS</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-1" data-id="element-248">
                  {['Todo lo incluido en el Combo 1', 'Todo lo incluido en el Combo 2', 'Manual extra: Usar Facebook e Instagram sin confundirte', 'Soporte técnico por email durante 30 días'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-249">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-250" />
                      <span className="text-slate-700 text-sm" data-id="element-251">{feature}</span>
                    </li>)}
                </ul>

                <a href="https://checkout3.rosetadigital.com/cart/45927680180409:1" className="block w-full bg-green-600 hover:bg-green-700 text-white text-center font-bold py-3 rounded-xl transition-colors" data-id="element-252">
                  Elegir este combo
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-253">
            <p className="text-slate-300 text-lg mb-8" data-id="element-254">
              Elegí el combo que mejor se adapte a tu objetivo actual. <br data-id="element-255" />
              Todos los materiales son digitales y de acceso inmediato.
            </p>

            {/* Secure Payment Badge - Below */}
            <div className="flex flex-col items-center gap-2 mb-8" data-id="element-256">
              <p className="text-slate-300 text-sm font-medium" data-id="element-257">
                Pago 100% seguro procesado por:
              </p>
              <img src="https://cdn.magicpatterns.com/uploads/avCwRnrSTuiqXUwcSckbkD/ML.jpg" alt="Mercado Libre - Pago Seguro" className="h-10 w-auto" data-id="element-258" />
            </div>

            <TrustBadge variant="dark" data-id="element-259" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white" data-id="element-260">
        <div className="max-w-3xl mx-auto" data-id="element-261">
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-8" data-id="element-262">
            Preguntas Frecuentes
          </h2>
          <FAQAccordion data-id="element-263" />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-50 py-12 px-4 border-t border-slate-200 text-center" data-id="element-264">
        <div className="max-w-4xl mx-auto" data-id="element-265">
          <p className="text-slate-600 font-medium mb-4" data-id="element-266">
            Manual Celular Sin Ayuda
          </p>
          <p className="text-sm text-slate-500 max-w-2xl mx-auto" data-id="element-267">
            Este producto no garantiza la obtención de resultados. Cualquier
            referencia al desempeño de una estrategia no debe ser interpretada
            como una garantía de resultados.
          </p>
          <p className="text-sm text-slate-500 mt-4" data-id="element-268">
            © 2024 Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Social Proof Popup */}
      <SocialProofPopup data-id="element-269" />
    </div>;
}